import { makeStyles, Typography } from "@material-ui/core"
import { Box, Stack } from "@mui/material"
import { FC, useState, useEffect } from "react"
import parsePhoneNumber from "libphonenumber-js"
import {
  ContentCard,
  Divider,
  Heading,
  ViraLink,
  ViraText,
} from "@ksana/vira-ui"
import { useNavigation } from "../../hooks/useNavigation"
import { useUser } from "../../hooks/useUser"
import { ComponentHeaderHeight, DefaultCountry } from "../../utils/constants"
import { colors } from "../../utils/theme"
import { GhostButton } from "../atoms/button/Button"
import ChevronLeftIcon from "../atoms/ChevronLeftIcon"
import ChangeEmailDialog from "../molecules/ChangeEmailDialog"
import ChangePasswordDialog from "../molecules/ChangePasswordDialog"
import EditPractitionerNameDialog from "../molecules/EditPractitionerNameDialog"
import { HeaderHeight } from "../molecules/Header"
import MobileVerifyDialog from "../organisms/MobileVerifyDialog"
import MobileUpdateDialog from "../organisms/MobileUpdateDialog"

const useStyles = makeStyles((theme) => ({
  accountSettings: {
    paddingTop: HeaderHeight,
    backgroundColor: colors.neutral.white,
    flex: 1,
  },
  header: {
    height: ComponentHeaderHeight,
    maxWidth: 1440,
    margin: "0 auto",
  },
  line: {
    borderBottom: `1px solid ${colors.neutral.clay}`,
  },
  content: {
    padding: theme.spacing(4),
    maxWidth: 1440,
    margin: "0 auto",
  },
  chevron: {
    marginRight: 4,
  },
  title: {
    color: colors.neutral.denim,
  },
  navigation: {
    height: 92,
    width: "fit-content",
    display: "flex",
    alignItems: "center",
    padding: 0,
    margin: 0,
    border: 0,
    backgroundColor: "transparent",
    cursor: "pointer",
    paddingLeft: theme.spacing(3),
    outline: "none",
  },
  settings: {
    marginTop: theme.spacing(3),
  },
  setting: {
    height: 68,
    width: "100%",
    maxWidth: 842,
    display: "flex",
    alignItems: "center",
    borderBottom: `1px solid ${colors.neutral.clay}`,
  },
  settingTitle: {
    width: 200,
    flexShrink: 0,
    fontWeight: 700,
    color: colors.neutral.denim,
  },
  settingValue: {
    color: colors.neutral.denim,
  },
  rest: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    minWidth: 0,
  },
}))

type SettingRow = {
  label: string
  value: string
  actionLabel: string
  onAction: () => void
}

const AccountSettings: FC<{
  /** Loop Client Detail pane — omit standalone header chrome. */
  embedded?: boolean
}> = ({ embedded = false }) => {
  const classes = useStyles()
  const { user } = useUser()
  const { toDashboard, toMyOrg } = useNavigation()
  const [openChangePassword, setOpenChangePassword] = useState<boolean>(false)
  const [openEditName, setOpenEditName] = useState<boolean>(false)
  const [openChangeEmail, setOpenChangeEmail] = useState<boolean>(false)
  const [openMobileVerifyDialog, setOpenMobileVerifyDialog] =
    useState<boolean>(false)
  const [openMobileUpdateDialog, setOpenMobileUpdateDialog] =
    useState<boolean>(false)
  const [mobile, setMobile] = useState<string>("")
  const [originalPhoneNumber, setOriginalPhoneNumber] = useState<string>("")

  useEffect(() => {
    if (user.phoneNumber) {
      const phoneNumber = parsePhoneNumber(user.phoneNumber, DefaultCountry)
      if (phoneNumber) {
        setMobile(phoneNumber.formatNational())
        setOriginalPhoneNumber(phoneNumber.formatNational())
      }
    }
  }, [user])

  const rows: SettingRow[] = [
    {
      label: "Name",
      value: [user?.firstName, user?.lastName].filter(Boolean).join(" "),
      actionLabel: "Edit",
      onAction: () => setOpenEditName(true),
    },
    {
      label: "Email",
      value: user?.email ?? "",
      actionLabel: "Change",
      onAction: () => setOpenChangeEmail(true),
    },
    {
      label: "Mobile number",
      value: mobile,
      actionLabel: "Change",
      onAction: () => setOpenMobileUpdateDialog(true),
    },
    {
      label: "Password",
      value: "················",
      actionLabel: "Change",
      onAction: () => setOpenChangePassword(true),
    },
  ]

  const legacySettingsBody = (
    <div className={classes.settings}>
      {rows.map((row) => (
        <div key={row.label} className={classes.setting}>
          <Typography variant="body1" className={classes.settingTitle}>
            {row.label}
          </Typography>
          <div className={classes.rest}>
            <Typography variant="body1" className={classes.settingValue}>
              {row.value}
            </Typography>
            <GhostButton onClick={row.onAction}>{row.actionLabel}</GhostButton>
          </div>
        </div>
      ))}
    </div>
  )

  const loopSettingsBody = (
    <ContentCard>
      {rows.map((row, index) => (
        <Box key={row.label}>
          {index > 0 ? <Divider /> : null}
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{
              py: 2,
              px: { xs: 0, sm: 0.5 },
              minHeight: 56,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Heading
              level="label"
              sx={{ width: { xs: 110, sm: 160 }, flexShrink: 0 }}
            >
              {row.label}
            </Heading>
            <ViraText
              tone="ink"
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: 13.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.value || "—"}
            </ViraText>
            <ViraLink
              component="button"
              type="button"
              onClick={row.onAction}
              sx={{ flexShrink: 0 }}
            >
              {row.actionLabel}
            </ViraLink>
          </Stack>
        </Box>
      ))}
    </ContentCard>
  )

  const dialogs = (
    <>
      <EditPractitionerNameDialog
        open={openEditName}
        onClose={() => setOpenEditName(false)}
      />
      <ChangeEmailDialog
        open={openChangeEmail}
        onClose={() => setOpenChangeEmail(false)}
      />
      <ChangePasswordDialog
        open={openChangePassword}
        onClose={() => setOpenChangePassword(false)}
        email={user.email}
      />
      <MobileVerifyDialog
        open={openMobileVerifyDialog}
        mobile={mobile}
        userId={user.id}
        openMobileUpdateDialog={() => {
          setOpenMobileVerifyDialog(false)
          setOpenMobileUpdateDialog(true)
        }}
        verifySuccess={() => {
          setOpenMobileVerifyDialog(false)
        }}
        closeCallback={() => {
          setOpenMobileVerifyDialog(false)
        }}
      />
      <MobileUpdateDialog
        open={openMobileUpdateDialog}
        mobile={mobile}
        openMobileVerifyDialog={(updateMobile: string) => {
          setOpenMobileUpdateDialog(false)
          if (updateMobile) {
            setMobile(updateMobile)
            setOpenMobileVerifyDialog(true)
          } else {
            setMobile(originalPhoneNumber)
          }
        }}
        closeCallback={() => {
          setMobile(originalPhoneNumber)
          setOpenMobileUpdateDialog(false)
        }}
      />
    </>
  )

  if (embedded) {
    return (
      <>
        {loopSettingsBody}
        {dialogs}
      </>
    )
  }

  return (
    <div className={classes.accountSettings}>
      <div className={classes.header}>
        <button
          className={classes.navigation}
          onClick={user?.isOrgAdmin ? toMyOrg : toDashboard}
        >
          <ChevronLeftIcon className={classes.chevron} />
          <Typography variant="h2" className={classes.title}>
            Account Settings
          </Typography>
        </button>
      </div>
      <div className={classes.line} />
      <div className={classes.content}>{legacySettingsBody}</div>
      {dialogs}
    </div>
  )
}

export default AccountSettings
